import React, { useEffect, useState } from 'react';
import { getApi, postApi } from 'views/services/api';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Typography,
  Paper,
  Box,
  Card,
  Container,
  Stack,
  Button,
  Chip,
  Avatar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  ButtonGroup,
  Tabs,
  Tab,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarTodayIcon,
  Badge as BadgeIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Hotel as HotelIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  CheckBox as CheckBoxIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Language as LanguageIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import Iconify from '../../ui-component/iconify';
import moment from 'moment';
import { toast } from 'react-toastify';

// 🔥 IMPORTAR EL COMPONENTE ConvertPreReservation
import ConvertPreReservation from './ConvertPreReservation';

const PreReservationManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [openConvert, setOpenConvert] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [openView, setOpenView] = useState(false);

  const [preReservationData, setPreReservationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [tabValue, setTabValue] = useState('all');
  const [rejectionReason, setRejectionReason] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // 🏨 IGUAL QUE EN Customer.js - EXACTAMENTE LA MISMA LÍNEA
  const hotel = JSON.parse(localStorage.getItem('hotelData')) || {};

  const [rowData, setRowData] = useState();

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Mostrar mensaje
  const showMessage = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // 🔄 FUNCIÓN PARA OBTENER PRE-RESERVACIONES - SIGUIENDO PATRÓN EXACTO Customer.js
  const fetchPreReservationData = async () => {
    // 🚨 Validar que existe hotelId
    if (!hotel?.hotelId) {
      setError('No se encontraron datos del hotel. Por favor, inicie sesión nuevamente.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('📡 Obteniendo pre-reservaciones...');
      console.log('🆔 Hotel ID:', hotel?.hotelId);

      // 🎯 SIGUIENDO EL PATRÓN EXACTO DE Customer.js
      const response = await getApi(`api/preReservation/viewallpreReservation/${hotel?.hotelId}`);
      console.log('response ==>', response);

      // 🎯 SIGUIENDO LA ESTRUCTURA EXACTA DE Customer.js
      setPreReservationData(response?.data?.preReservationData || []);
      console.log('✅ Pre-reservaciones cargadas:', response?.data?.preReservationData?.length || 0);
    } catch (error) {
      console.log('❌ Error:', error);
      setPreReservationData([]);
      setError('Error al cargar las pre-reservaciones. Por favor, intente nuevamente.');
      toast.error('Error al cargar pre-reservaciones');
    } finally {
      setLoading(false);
    }
  };

  // 🎯 SIGUIENDO EL PATRÓN DE Customer.js - useEffect con dependencias
  useEffect(() => {
    fetchPreReservationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConvert, openReject]);

  //functions for dialog boxes - SIGUIENDO PATRÓN Customer.js
  const handleOpenConvert = () => setOpenConvert(true);
  const handleCloseConvert = () => setOpenConvert(false);

  const handleOpenReject = () => setOpenReject(true);
  const handleCloseReject = () => setOpenReject(false);

  const handleOpenView = () => setOpenView(true);
  const handleCloseView = () => setOpenView(false);

  // 🔥 FUNCIÓN CALLBACK CUANDO SE CONVIERTE EXITOSAMENTE
  const handleConvertSuccess = () => {
    console.log('🎉 Conversión exitosa, recargando lista...');
    showMessage('🎉 Pre-reserva convertida exitosamente!', 'success');
    handleCloseConvert();
    fetchPreReservationData();

    // 🔧 Opcional: Cambiar filtro a "Convertidas" para mostrar el resultado
    setTimeout(() => {
      setFilterStatus('converted');
      setTabValue('converted');
    }, 1000);
  };

  // ❌ Confirmar rechazo
  const confirmRejectReservation = async () => {
    try {
      setLoading(true);

      if (!rejectionReason.trim()) {
        showMessage('❌ Por favor ingresa un motivo para el rechazo', 'error');
        return;
      }

      const rejectData = {
        preReservationId: rowData._id,
        reason: rejectionReason.trim(),
        hotelId: hotel?.hotelId
      };

      console.log('📤 Enviando rechazo:', rejectData);

      const response = await postApi('api/preReservation/reject', rejectData);

      console.log('✅ Respuesta del servidor:', response);

      showMessage('❌ Pre-reserva rechazada exitosamente\n\n📱 Cliente notificado por WhatsApp', 'success');
      setRejectionReason('');
      handleCloseReject();
      await fetchPreReservationData();
    } catch (error) {
      console.error('❌ Error rechazando reserva:', error);

      let errorMsg = 'Error desconocido';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      showMessage('❌ Error al rechazar reserva:\n\n' + errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // 🎨 Función para obtener color del estado
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
      case 'pendiente':
        return 'warning';
      case 'confirmed':
      case 'confirmado':
        return 'info';
      case 'converted':
      case 'convertida':
        return 'success';
      case 'rejected':
      case 'rechazada':
        return 'error';
      case 'expired':
      case 'expirada':
        return 'default';
      default:
        return 'warning';
    }
  };

  // 🎨 Función para obtener color del tipo de estadía
  const getStayTypeColor = (stayType) => {
    switch (stayType?.toLowerCase()) {
      case 'noche':
        return 'primary';
      case 'parcial':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // 📊 Función helper para manejar datos del cliente
  const getCustomerInfo = (item) => {
    if (item.customerDetails && typeof item.customerDetails === 'object') {
      return {
        firstName: item.customerDetails.firstName || item.firstName,
        lastName: item.customerDetails.lastName || item.lastName,
        email: item.customerDetails.email || item.email,
        phone: item.customerDetails.phoneNumber || item.phoneNumber,
        idCard: item.customerDetails.idcardNumber || item.idNumber,
        fullName: `${item.customerDetails.firstName || item.firstName || ''} ${item.customerDetails.lastName || item.lastName || ''}`.trim()
      };
    }

    return {
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      phone: item.phoneNumber,
      idCard: item.idNumber,
      fullName: `${item.firstName || ''} ${item.lastName || ''}`.trim()
    };
  };

  // 🔍 Función de filtrado
  const applyFilter = () => {
    if (filterStatus === 'all') {
      return preReservationData;
    }
    return preReservationData.filter((reservation) => reservation.status?.toLowerCase() === filterStatus.toLowerCase());
  };

  // Cambio de tab/filtro
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setFilterStatus(newValue);
  };

  // 📊 Calcular estadísticas (MEJORADAS)
  const pendingCount = preReservationData.filter((item) => item.status === 'pending' || !item.status).length;

  const convertedCount = preReservationData.filter((item) => item.status === 'converted').length;

  const rejectedCount = preReservationData.filter((item) => item.status === 'rejected').length;

  const todayCount = preReservationData.filter((item) => {
    const today = new Date();
    const itemDate = new Date(item.createdAt);
    return itemDate.toDateString() === today.toDateString();
  }).length;

  // 📋 Definir columnas para DataGrid
  const getColumns = () => {
    const baseColumns = [
      {
        field: 'customer',
        headerName: 'Cliente',
        flex: 1.5,
        minWidth: 200,
        renderCell: (params) => {
          const customerInfo = getCustomerInfo(params.row);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  onClick={() => {
                    setRowData(params.row);
                    handleOpenView();
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {customerInfo.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {customerInfo.phone}
                </Typography>
              </Box>
            </Box>
          );
        }
      },
      {
        field: 'email',
        headerName: 'Contacto',
        flex: 1.2,
        minWidth: 180,
        renderCell: (params) => {
          const customerInfo = getCustomerInfo(params.row);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">{customerInfo.email || 'No email'}</Typography>
            </Box>
          );
        }
      },
      {
        field: 'checkInDate',
        headerName: 'Fecha Deseada',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Typography variant="body2">{moment(params.value).format('DD/MM/YYYY HH:mm')}</Typography>
            </Box>
          );
        }
      },
      {
        field: 'roomTypeName',
        headerName: 'Habitación',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" fontWeight="bold">
                {params.row.roomTypeName || 'No especificada'}
              </Typography>
              {params.row.price && (
                <Typography variant="caption" color="text.secondary">
                  ${params.row.price}
                </Typography>
              )}
            </Box>
          );
        }
      },
      {
        field: 'stayType',
        headerName: 'Tipo',
        flex: 0.8,
        minWidth: 100,
        renderCell: (params) => {
          return (
            <Chip
              label={params.value === 'noche' ? 'Noche' : '3 Horas'}
              color={getStayTypeColor(params.value)}
              variant="outlined"
              size="small"
            />
          );
        }
      },
      {
        field: 'channel',
        headerName: 'Canal',
        flex: 0.8,
        minWidth: 100,
        renderCell: (params) => {
          const isWeb = params.value === 'website';
          return (
            <Chip
              icon={isWeb ? <LanguageIcon fontSize="small" /> : <WhatsAppIcon fontSize="small" />}
              label={isWeb ? 'Website' : 'WhatsApp'}
              color={isWeb ? 'info' : 'success'}
              variant="outlined"
              size="small"
            />
          );
        }
      },
      {
        field: 'status',
        headerName: 'Estado',
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => {
          const statusLabels = {
            pending: 'Pendiente',
            confirmed: 'Confirmada',
            converted: 'Convertida',
            rejected: 'Rechazada',
            expired: 'Expirada'
          };

          return (
            <Chip
              label={statusLabels[params.value?.toLowerCase()] || 'Pendiente'}
              color={getStatusColor(params.value)}
              variant="filled"
              size="small"
            />
          );
        }
      },
      {
        field: 'createdAt',
        headerName: 'Recibida',
        flex: 1,
        minWidth: 140,
        renderCell: (params) => {
          return <Typography variant="body2">{moment(params.value).format('DD/MM HH:mm')}</Typography>;
        }
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        flex: 1.5,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => {
          const canTakeAction = params.row.status === 'pending' || !params.row.status;

          return (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Ver detalles">
                <IconButton
                  size="small"
                  onClick={() => {
                    setRowData(params.row);
                    handleOpenView();
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {canTakeAction && (
                <>
                  <Tooltip title="Convertir a reserva">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => {
                        setRowData(params.row);
                        handleOpenConvert();
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Rechazar">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setRowData(params.row);
                        handleOpenReject();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          );
        }
      }
    ];

    // Para pantallas móviles, mostrar menos columnas
    if (isMobile) {
      return baseColumns.filter((col) => ['customer', 'status', 'actions'].includes(col.field));
    }

    return baseColumns;
  };

  // 🚨 Si no hay datos del hotel, mostrar error
  if (!hotel?.hotelId) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="h6">Error de Autenticación</Typography>
          <Typography>No se encontraron datos del hotel. Por favor, inicie sesión nuevamente.</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl">
        {/* 🎯 Panel de estadísticas */}
        <Box mb={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '16px',
              background: 'linear-gradient(to right, #f5f7fa, #ffffff)',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
              overflow: 'hidden'
            }}
          >
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} divider={!isMobile && <Divider orientation="vertical" flexItem />}>
              {/* Título y resumen */}
              <Box flex={1}>
                <Typography variant="h4" color="primary" gutterBottom>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <BadgeIcon sx={{ mr: 1 }} />
                    Pre-Reservaciones - {hotel?.hotelName || 'Hotel'}
                  </Box>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Administra las solicitudes de reserva por WhatsApp en un solo lugar.
                </Typography>
              </Box>

              {/* Tarjetas de estadísticas */}
              <Stack direction={isMobile ? 'column' : 'row'} spacing={2} flex={2} width="100%">
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#fff8e1',
                    border: '1px solid #ffecb3',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { transform: 'translateY(-2px)' },
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() => {
                    setFilterStatus('pending');
                    setTabValue('pending');
                  }}
                >
                  <PendingIcon sx={{ fontSize: 40, color: '#f57c00', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#f57c00">
                    {pendingCount}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#f57c00">
                    Pendientes
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#e8f5e9',
                    border: '1px solid #c8e6c9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { transform: 'translateY(-2px)' },
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() => {
                    setFilterStatus('converted');
                    setTabValue('converted');
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#2e7d32">
                    {convertedCount}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#2e7d32">
                    Convertidas
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#e3f2fd',
                    border: '1px solid #bbdefb',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { transform: 'translateY(-2px)' },
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() => {
                    setFilterStatus('all');
                    setTabValue('all');
                  }}
                >
                  <HotelIcon sx={{ fontSize: 40, color: '#1565c0', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#1565c0">
                    {preReservationData.length}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#1565c0">
                    Total
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    flex: 1,
                    borderRadius: '12px',
                    bgcolor: '#fce4ec',
                    border: '1px solid #f8bbd9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <StarIcon sx={{ fontSize: 40, color: '#c2185b', mb: 1 }} />
                  <Typography variant="h4" align="center" color="#c2185b">
                    {todayCount}
                  </Typography>
                  <Typography variant="subtitle2" align="center" color="#c2185b">
                    Hoy
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* Tabs de navegación */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 2px 10px 0 rgba(0,0,0,0.05)'
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="estado pre-reservaciones"
          >
            <Tab
              value="all"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <HotelIcon fontSize="small" />
                  <Typography>Todas</Typography>
                  <Chip label={preReservationData.length} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />
                </Stack>
              }
            />
            <Tab
              value="pending"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PendingIcon fontSize="small" />
                  <Typography>Pendientes</Typography>
                  <Chip label={pendingCount} size="small" color="warning" sx={{ height: 20, fontSize: 12 }} />
                </Stack>
              }
            />
            <Tab
              value="converted"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleIcon fontSize="small" />
                  <Typography>Convertidas</Typography>
                  <Chip label={convertedCount} size="small" color="success" sx={{ height: 20, fontSize: 12 }} />
                </Stack>
              }
            />
            <Tab
              value="rejected"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CloseIcon fontSize="small" />
                  <Typography>Rechazadas</Typography>
                  <Chip label={rejectedCount} size="small" color="error" sx={{ height: 20, fontSize: 12 }} />
                </Stack>
              }
            />
          </Tabs>
        </Paper>

        {/* Acciones secundarias */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5">
            {filterStatus === 'all'
              ? 'Todas las Pre-Reservaciones'
              : filterStatus === 'pending'
                ? 'Pre-Reservaciones Pendientes'
                : filterStatus === 'converted'
                  ? 'Pre-Reservaciones Convertidas'
                  : 'Pre-Reservaciones Rechazadas'}
          </Typography>

          <ButtonGroup variant="outlined" size="small">
            <Tooltip title="Refrescar datos">
              <Button
                onClick={fetchPreReservationData}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                {loading ? 'Cargando' : 'Refrescar'}
              </Button>
            </Tooltip>
            <Tooltip title="Filtros adicionales">
              <Button startIcon={<FilterListIcon />}>Filtrar</Button>
            </Tooltip>
            {/* Botón de pendientes */}
            {pendingCount > 0 && (
              <Tooltip title="Ver pendientes">
                <Button
                  color="warning"
                  startIcon={<BadgeIcon />}
                  onClick={() => {
                    setFilterStatus('pending');
                    setTabValue('pending');
                  }}
                >
                  {pendingCount} Pendientes
                </Button>
              </Tooltip>
            )}
          </ButtonGroup>
        </Stack>

        {/* Mensaje de error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Contenido principal */}
        <Box width="100%">
          <Card
            sx={{
              height: '70vh',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)'
            }}
          >
            {/* Encabezado de la tabla con contador */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Pre-Reservaciones ({applyFilter()?.length || 0})</Typography>

              {/* Botón de actualización manual */}
              <Tooltip title="Actualizar datos">
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={fetchPreReservationData}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Iconify icon="eva:refresh-fill" />}
                >
                  {loading ? 'Cargando...' : 'Actualizar'}
                </Button>
              </Tooltip>
            </Stack>

            {/* DataGrid con datos de pre-reservaciones */}
            {loading && !preReservationData.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                <Stack spacing={2} alignItems="center">
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">
                    Cargando pre-reservaciones...
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <DataGrid
                rows={applyFilter() || []}
                columns={getColumns()}
                checkboxSelection
                getRowId={(row) => row?._id || Math.random().toString()}
                slots={{
                  toolbar: GridToolbar,
                  noRowsOverlay: () => (
                    <Stack height="100%" alignItems="center" justifyContent="center" spacing={2} p={5}>
                      {filterStatus === 'pending' ? (
                        <PendingIcon sx={{ fontSize: 60, color: '#e0e0e0' }} />
                      ) : filterStatus === 'converted' ? (
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#e0e0e0' }} />
                      ) : (
                        <BadgeIcon sx={{ fontSize: 60, color: '#e0e0e0' }} />
                      )}
                      <Typography variant="h6" color="text.secondary">
                        No hay pre-reservaciones{' '}
                        {filterStatus === 'all'
                          ? ''
                          : filterStatus === 'pending'
                            ? 'pendientes'
                            : filterStatus === 'converted'
                              ? 'convertidas'
                              : 'rechazadas'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Las solicitudes de WhatsApp aparecerán aquí
                      </Typography>
                    </Stack>
                  )
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: {
                      debounceMs: 500,
                      placeholder: 'Buscar pre-reservaciones...'
                    }
                  }
                }}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 }
                  },
                  sorting: {
                    sortModel: [{ field: 'createdAt', sort: 'desc' }]
                  }
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                density="standard"
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: theme.palette.background.neutral,
                    borderRadius: '8px 8px 0 0',
                    fontWeight: 'bold'
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    backgroundColor: theme.palette.background.paper
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: 'none',
                    backgroundColor: theme.palette.background.neutral,
                    borderRadius: '0 0 8px 8px'
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)'
                    },
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  },
                  '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none'
                  }
                }}
              />
            )}
          </Card>
        </Box>

        {/* Modales */}
        <ConvertPreReservation
          open={openConvert}
          handleClose={handleCloseConvert}
          preReservationData={rowData}
          onSuccess={handleConvertSuccess}
        />

        {/* Modal de rechazo */}
        <Dialog open={openReject} onClose={handleCloseReject} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CloseIcon color="error" />
              <Typography variant="h6">Rechazar Pre-Reserva</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Motivo del rechazo"
              multiline
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
              placeholder="Explica brevemente por qué se rechaza la pre-reserva..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReject} disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={confirmRejectReservation}
              variant="contained"
              color="error"
              disabled={loading || !rejectionReason.trim()}
              startIcon={loading ? <RefreshIcon /> : <CloseIcon />}
            >
              {loading ? 'Procesando...' : 'Rechazar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de vista de detalles */}
        <Dialog open={openView} onClose={handleCloseView} maxWidth="md" fullWidth>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <VisibilityIcon />
              <Typography variant="h6">Detalles de Pre-Reservación</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {rowData && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Información Personal
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="action" />
                      <Typography>{getCustomerInfo(rowData).fullName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" />
                      <Typography>{getCustomerInfo(rowData).phone}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="action" />
                      <Typography>{getCustomerInfo(rowData).email || 'No email'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon color="action" />
                      <Typography>{getCustomerInfo(rowData).idCard}</Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Detalles de Reserva
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon color="action" />
                      <Typography>{moment(rowData.checkInDate).format('DD/MM/YYYY HH:mm')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HotelIcon color="action" />
                      <Typography>{rowData.stayType === 'noche' ? 'Noche completa' : 'Tiempo parcial (3h)'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon color="action" />
                      <Typography>Puntos: {rowData.pointsEarned || 0}</Typography>
                    </Box>
                    <Chip label={rowData.status || 'Pendiente'} color={getStatusColor(rowData.status)} sx={{ alignSelf: 'flex-start' }} />

                    {rowData.convertedAt && (
                      <Typography variant="body2" color="success.main">
                        ✅ Convertida: {moment(rowData.convertedAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    )}
                    {rowData.rejectedAt && (
                      <Typography variant="body2" color="error.main">
                        ❌ Rechazada: {moment(rowData.rejectedAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseView}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default PreReservationManager;
